<script setup>
defineProps({
  label: { type: String, required: true },
  count: { type: Number, default: null },
  active: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
});

defineEmits(['select']);

const onKeydown = event => {
  if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
  const list = event.currentTarget.closest('[role="tablist"]');
  const tabs = [...list.querySelectorAll('[role="tab"]:not(:disabled)')];
  const currentIndex = tabs.indexOf(event.currentTarget);
  const isRtl = getComputedStyle(list).direction === 'rtl';
  const direction = (event.key === 'ArrowRight' ? 1 : -1) * (isRtl ? -1 : 1);
  let nextIndex = (currentIndex + direction + tabs.length) % tabs.length;
  if (event.key === 'Home') nextIndex = 0;
  if (event.key === 'End') nextIndex = tabs.length - 1;
  event.preventDefault();
  tabs[nextIndex].focus({ preventScroll: true });
  tabs[nextIndex].scrollIntoView({ block: 'nearest', inline: 'nearest' });
  tabs[nextIndex].click();
};
</script>

<template>
  <button
    type="button"
    role="tab"
    :aria-selected="active"
    :tabindex="active ? 0 : -1"
    :disabled="disabled"
    class="relative shrink-0 overflow-visible rounded-md border-0 bg-transparent px-2.5 text-start font-semibold leading-5 outline-none transition-colors duration-200 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-n-brand disabled:cursor-not-allowed disabled:opacity-40 motion-reduce:transition-none"
    :class="[
      compact ? 'h-10 py-2.5 text-sm' : 'h-11 py-3 text-base',
      active
        ? 'text-n-brand enabled:hover:text-n-brand'
        : 'text-n-slate-11 enabled:hover:bg-n-alpha-2',
    ]"
    @click="$emit('select')"
    @keydown="onKeydown"
  >
    <span class="relative inline-block max-w-full whitespace-nowrap">
      {{ label }}{{ count != null ? ` (${count})` : '' }}
    </span>
    <span
      v-if="active"
      class="pointer-events-none absolute bottom-0 start-0 h-1 w-full rounded-t bg-n-brand"
      aria-hidden="true"
    />
  </button>
</template>
